import { Injectable, inject } from '@angular/core';
import { ApiService, GenericResponseCode } from '@brighthub/api';
import { Observable, catchError, map, shareReplay, throwError } from 'rxjs';

const API_NAME = 'api-internal';
const SYSTEM_CODE_PATH = '/system-code-value';

interface SystemCodeEnvelope {
  result?: { code?: string; message?: string };
  payload?: unknown;
}

export interface SystemCodeCatalogValue {
  code: string;
  label: string;
  systemCodeValueId: number;
  abbreviation: string | null;
}

export type SystemCodeCatalog = Readonly<
  Record<string, readonly SystemCodeCatalogValue[]>
>;

/**
 * App-root catalogue for active System Code values.
 *
 * The unfiltered endpoint is intentionally loaded once after the first
 * subscriber and replayed for the lifetime of the SPA. Failed loads are not
 * retained, allowing a later Retry to issue a fresh request.
 */
@Injectable({ providedIn: 'root' })
export class SystemCodeCatalogService {
  private readonly _api = inject(ApiService);
  private _catalog$: Observable<SystemCodeCatalog> | undefined;

  /** Explicit user retry replaces the cached catalog, including incomplete successful responses. */
  reload(): Observable<SystemCodeCatalog> {
    this._catalog$ = undefined;
    return this.load();
  }

  load(): Observable<SystemCodeCatalog> {
    if (this._catalog$) return this._catalog$;

    const request$ = this._api
      .get<SystemCodeEnvelope>(API_NAME, SYSTEM_CODE_PATH)
      .pipe(
        map((response) => this._payload(response)),
        map((payload) => this._normalize(payload)),
        catchError((error) => {
          if (this._catalog$ === request$) this._catalog$ = undefined;
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this._catalog$ = request$;
    return request$;
  }

  values(
    catalog: SystemCodeCatalog,
    codeType: string,
  ): readonly SystemCodeCatalogValue[] {
    const normalizedType = codeType.trim().toLowerCase();
    const values = catalog[normalizedType];
    if (!values?.length) {
      throw new Error(`System Code catalog omitted ${normalizedType}.`);
    }
    return values;
  }

  private _payload(response: SystemCodeEnvelope): unknown {
    if (
      response?.result?.code !== GenericResponseCode.SERVICE_OK ||
      response.payload == null
    ) {
      throw new Error(
        response?.result?.message ||
          'System Code API returned an invalid response.',
      );
    }
    return response.payload;
  }

  private _normalize(payload: unknown): SystemCodeCatalog {
    const payloadRecord = this._record(payload);
    const container =
      payloadRecord?.['systemCodeValues'] ??
      payloadRecord?.['systemCodeValueGroups'] ??
      payload;
    const groups = new Map<string, Map<string, SystemCodeCatalogValue>>();

    if (Array.isArray(container)) {
      for (const candidate of container) {
        const record = this._record(candidate);
        const codeType = this._string(candidate, 'codeType', 'key');
        const values = record?.['values'];
        if (codeType && Array.isArray(values)) {
          this._addGroup(groups, codeType, values);
        }
      }
    } else {
      for (const [codeType, values] of Object.entries(
        this._record(container) ?? {},
      )) {
        if (Array.isArray(values)) this._addGroup(groups, codeType, values);
      }
    }

    if (!groups.size) {
      throw new Error('System Code API returned no active code groups.');
    }

    return Object.fromEntries(
      [...groups].map(([codeType, values]) => [codeType, [...values.values()]]),
    );
  }

  private _addGroup(
    groups: Map<string, Map<string, SystemCodeCatalogValue>>,
    codeType: string,
    resources: readonly unknown[],
  ): void {
    const normalizedType = codeType.trim().toLowerCase();
    if (!normalizedType) return;
    const values = groups.get(normalizedType) ?? new Map();
    groups.set(normalizedType, values);

    for (const resource of resources) {
      const code = this._string(resource, 'key', 'code')?.toUpperCase();
      const label = this._string(resource, 'displayValue', 'value');
      const systemCodeValueId = this._number(resource, 'systemCodeValueId');
      if (!code || !label || systemCodeValueId == null || values.has(code)) {
        continue;
      }
      values.set(code, {
        code,
        label,
        systemCodeValueId,
        abbreviation: this._string(resource, 'abbreviation'),
      });
    }
  }

  private _record(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }

  private _string(value: unknown, ...keys: string[]): string | null {
    const record = this._record(value);
    for (const key of keys) {
      const candidate = record?.[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return null;
  }

  private _number(value: unknown, key: string): number | null {
    const candidate = this._record(value)?.[key];
    return typeof candidate === 'number' && Number.isFinite(candidate)
      ? candidate
      : null;
  }
}
